import type { LucideIcon } from "lucide-react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { useI18n } from "../i18n";
import type { MetricValue } from "../shared/contracts";

interface MetricCardProps {
  label: string;
  metric: MetricValue;
  icon: LucideIcon;
  tone?: "green" | "blue" | "amber" | "red" | "ink";
}

function formatMetric(metric: MetricValue, locale: string) {
  if (metric.value === null) return "—";

  switch (metric.unit) {
    case "percent":
      return `${metric.value.toFixed(1)}%`;
    case "currency":
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(metric.value);
    case "seconds":
      return `${metric.value.toFixed(1)} s`;
    case "milliseconds":
      return `${Math.round(metric.value)} ms`;
    case "ratio":
      return metric.value.toFixed(2);
    case "position":
      return metric.value.toFixed(1);
    case "score":
      return `${Math.round(metric.value)}/100`;
    default:
      return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 0,
      }).format(metric.value);
  }
}

function getDelta(metric: MetricValue) {
  if (
    metric.value === null ||
    metric.previous === null ||
    metric.previous === undefined ||
    metric.previous === 0
  ) {
    return null;
  }

  return ((metric.value - metric.previous) / Math.abs(metric.previous)) * 100;
}

export function MetricCard({
  label,
  metric,
  icon: Icon,
  tone = "ink",
}: MetricCardProps) {
  const { text, locale } = useI18n();
  const delta = getDelta(metric);
  const TrendIcon =
    delta === null || delta === 0
      ? Minus
      : delta > 0
        ? ArrowUpRight
        : ArrowDownRight;

  return (
    <article className="metric-card">
      <div className={`metric-icon tone-${tone}`}>
        <Icon size={18} strokeWidth={1.9} />
      </div>
      <div className="metric-copy">
        <span className="metric-label">{label}</span>
        <strong className="metric-value">{formatMetric(metric, locale)}</strong>
      </div>
      <div
        className={`metric-delta ${
          delta === null ? "neutral" : delta >= 0 ? "positive" : "negative"
        }`}
      >
        <TrendIcon size={14} />
        {delta === null
          ? text.dashboard.noComparison
          : `${Math.abs(delta).toFixed(1)}%`}
      </div>
    </article>
  );
}
